/**
 * The exact-item page: Design Bible 12.1, plus the channel rules in
 * MASTER_SPEC 9.3.
 *
 * The eBay variant is where a storefront usually lies. A greyed-out `Buy now`
 * beside a "sorry, unavailable" tooltip reads as a broken site, and Design
 * Bible 14 makes that a rejectable defect. So an `ACTIVE_EBAY` item renders a
 * genuinely different action region: one working `Buy on eBay` link, a sentence
 * stating where checkout happens, and no cart control, no quote control, and no
 * PayPal reference anywhere in the markup, in the keyboard order, or in any
 * code path this page can reach.
 *
 * The inverse rule matters just as much and is enforced by the public contract
 * rather than here: `parsePublicItem` refuses a payload where an eBay-channel
 * listing claims to be checkout eligible, so this view cannot be handed a
 * contradiction to resolve.
 */

import { type ReactNode, useEffect, useState } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import { useAnnounce } from "../components/Announcer.js";
import { ItemIdentity, ObjectDossier } from "../components/ObjectDossier.js";
import { SlabFrame } from "../components/SlabFrame.js";
import { PublicImage } from "../components/PublicImage.js";
import { PhotoViewer } from "../components/PhotoViewer.js";
import { ActionButton, Money, Quoted, StatusChip } from "../components/primitives.js";
import { CheckoutUnavailableBlock, ResourceGate } from "../components/states.js";
import { itemPath } from "../lib/coreRoutes.js";
import { availabilityLabel } from "../lib/labels.js";
import { parsePublicItem, type PublicItem } from "../lib/publicContract.js";
import { productHref } from "../lib/routes.js";
import { useResource } from "../lib/useResource.js";
import { InlineInquiry } from "./InquiryForm.js";
import { InternalLink } from "../components/InternalLink.js";

export function ItemView({ itemID }: { itemID: string }): ReactNode {
  const { transport, preload } = useStorefront();
  const path = itemPath(itemID);
  const initial = preload !== null && preload.path === path ? (preload.item ?? null) : null;

  const resource = useResource<PublicItem>(transport, {
    path,
    parse: parsePublicItem,
    requirement: "this item",
    initial
  });

  return (
    <ResourceGate resource={resource} skeletonCards={1} subject="item" waitingFor="this item">
      {(item, stale) => <ItemDetail item={item} key={item.physical_item_id} stale={stale} />}
    </ResourceGate>
  );
}

function ItemDetail({ item, stale }: { item: PublicItem; stale: boolean }): ReactNode {
  const { rememberItem } = useStorefront();
  const announce = useAnnounce();
  useEffect(() => { if (!stale) rememberItem(item); }, [item, stale, rememberItem]);
  const availability = availabilityLabel(item.availability, item.checkout_eligible);
  const [activeMedia, setActiveMedia] = useState(0);
  const [enlarged, setEnlarged] = useState(false);
  const cover = item.public_derivatives[activeMedia] ?? item.public_derivatives[0];

  return (
    <article className="item">
      <div className="item-heading stack">
        <nav aria-label="Breadcrumb" className="breadcrumbs">
          <InternalLink href="/catalog">Catalogue</InternalLink>
          <span aria-hidden="true">/</span>
          <InternalLink href={productHref(item.product_id)}>{item.platform} release</InternalLink>
        </nav>
        <div>
          <h1 className="t-display-l">{item.title}</h1>
          <p className="t-body-l t-secondary">
            {item.platform} · {item.region} · {item.edition_variant}
          </p>
        </div>
      </div>
      <div className="item-media">
        {/* 4.4: `slab-seat` fires once, on entering the item view. */}
        <SlabFrame
          certNumber={item.cert_number}
          editionVariant={item.edition_variant}
          gradingCompany={item.grading_company}
          grade={item.grade}
          platform={item.platform}
          seat
          size="lg"
          sku={item.sku}
        >
          {cover === undefined ? (
            <p className="t-body-m t-secondary">
              Photographs are not available yet. Ask about this exact item before buying.
            </p>
          ) : (
            <PublicImage
              alt={`Approved photograph ${String(activeMedia + 1)} of ${item.title}, SKU ${item.sku}`}
              className="item-image"
              src={cover}
            />
          )}
        </SlabFrame>
        {item.public_derivatives.length > 1 ? (
          <div aria-label="Photographs of this exact item" className="thumbs" role="group">
            {item.public_derivatives.map((url, index) => (
              <button
                aria-current={index === activeMedia}
                aria-label={`Show photograph ${String(index + 1)} of ${String(item.public_derivatives.length)}`}
                className={index === activeMedia ? "thumb thumb--current" : "thumb"}
                key={url}
                onClick={() => {
                  setActiveMedia(index);
                }}
                type="button"
              >
                <img alt="" decoding="async" loading="lazy" src={url} />
              </button>
            ))}
          </div>
        ) : null}
        {cover === undefined ? null : (
          <div className="gallery-caption">
            <p className="t-body-s t-secondary">Photo {activeMedia + 1} of {item.public_derivatives.length} · This exact copy</p>
            <button className="btn btn--secondary gallery-enlarge" onClick={() => { setEnlarged(true); }} type="button">Enlarge photo</button>
          </div>
        )}
      </div>

      <div className="item-detail stack">
        <section className="purchase-evidence" aria-label="Inspect this copy before buying">
          <h2 className="t-display-xs">The copy you will receive</h2>
          <ItemIdentity title={item.title} sku={item.sku} gradingCompany={item.grading_company} grade={item.grade} certNumber={item.cert_number} />
          {item.cert_number === undefined ? (
            item.grading_company === undefined ? null : <a href="#inquiry-heading">Request the certification number</a>
          ) : <button type="button" className="btn btn--quiet" onClick={() => {
            if (navigator.clipboard === undefined) {
              announce("Copy unavailable. Select the certification number above to copy it.");
              return;
            }
            void navigator.clipboard.writeText(item.cert_number ?? "").then(
              () => { announce("Certification number copied."); },
              () => { announce("Copy unavailable. Select the certification number above to copy it."); }
            );
          }}>Copy certification number</button>}
          <Quoted label="Condition of this copy" text={item.condition} />
        </section>
        <div className="price-block">
          <Money minor={item.price_minor} />
          <StatusChip label={availability.label} tone={availability.tone} />
          <p className="t-body-s t-secondary">{availability.detail}</p>
        </div>

        {item.availability === "ACTIVE_EBAY" ? (
          <EbayActions item={item} />
        ) : (
          <WebsiteActions item={item} stale={stale} />
        )}
        <div className="item-inspect-links">
          <a href="#dossier-copy">Inspect grade &amp; condition</a>
          <InternalLink href={productHref(item.product_id)}>Compare copies of this release</InternalLink>
        </div>
      </div>
      {enlarged ? <PhotoViewer item={item} index={activeMedia} onChange={setActiveMedia} onClose={() => { setEnlarged(false); }} /> : null}
      <ObjectDossier item={item} />
      <div className="item-inquiry">
        <InlineInquiry
          heading="Ask about this item"
          intro={
            item.availability === "ACTIVE_EBAY"
              ? "Questions about the grade, the slab, or the photographs are welcome. Buying this item happens on eBay."
              : "Questions about the grade, the slab, the certification number, or shipping are welcome before you buy."
          }
          physicalItemID={item.physical_item_id}
        />
      </div>
    </article>
  );
}

/**
 * MASTER_SPEC 9.3 / Design Bible 12.1: one outward link, a statement of where
 * checkout happens, and copy that does not invite moving the transaction off
 * eBay. `rel="noopener"` because this is the only outward navigation in the app.
 */
function EbayActions({ item }: { item: PublicItem }): ReactNode {
  return (
    <div className="actions" data-testid="ebay-actions">
      <a className="btn btn--primary" href={item.public_url} rel="noopener noreferrer" target="_blank">
        Buy on eBay
      </a>
      <p className="t-body-s t-secondary">
        This item is listed on eBay. Checkout, payment, and buyer protection all happen there.
      </p>
    </div>
  );
}

function WebsiteActions({ item, stale }: { item: PublicItem; stale: boolean }): ReactNode {
  const { config, cart, addToCart, navigate, handoff } = useStorefront();
  const announce = useAnnounce();
  const inCart = cart.includes(item.physical_item_id);
  const [cartProblem, setCartProblem] = useState<string | null>(null);

  if (stale) {
    return <p className="state state--accent" role="status">Reconnect and reload this item to check its price and availability before buying.</p>;
  }

  if (!item.checkout_eligible) {
    return (
      <div className="actions">
        <p className="t-body-m">
          This item is published but is not for sale on this site at the moment. Asking about it is
          the fastest way to hear when that changes.
        </p>
      </div>
    );
  }

  if (!config.checkout_enabled) {
    return <CheckoutUnavailableBlock inquiryHref="#inquiry-heading" reason={config.problem ?? "Checkout is switched off."} />;
  }

  if (handoff.quoteID !== null) {
    return (
      <div className="actions">
        <p className="t-body-m">You have a checkout in progress. Review its status before adding another copy.</p>
        <InternalLink className="btn btn--primary" href="/checkout">Return to checkout</InternalLink>
      </div>
    );
  }

  return (
    <div className="actions purchase-actions" data-testid="website-actions">
      <div className="purchase-buttons">
        <ActionButton
          id="add-to-cart"
          onClick={() => {
            if (inCart) {
              navigate("/cart");
              return;
            }
            const outcome = addToCart(item.physical_item_id);
            if (outcome === "added") {
              announce(`${item.title}, SKU ${item.sku}, added to your cart.`);
            } else if (outcome === "already") {
              announce("This exact item is already in your cart. There is only one of it.");
            } else {
              setCartProblem("Your cart is full. Remove an item from your cart to add this copy.");
              announce("Your cart is full. Review your cart to make room.");
            }
          }}
          variant={inCart ? "secondary" : "primary"}
        >
          {inCart ? "In your cart" : "Add to cart"}
        </ActionButton>
        <button
          className="btn btn--secondary"
          onClick={() => {
            if (addToCart(item.physical_item_id) === "full") {
              setCartProblem("Your cart is full. Remove an item from your cart to add this copy.");
              announce("Your cart is full. This item was not added.");
              return;
            }
            navigate("/checkout");
          }}
          type="button"
        >
          Buy now
        </button>
      </div>
      {cartProblem === null ? null : <p className="field-problem" role="alert">{cartProblem} <InternalLink href="/cart">Review your cart</InternalLink></p>}
      <p className="t-body-s t-secondary">
        Review shipping and the full total before approving payment on PayPal. No account needed.
      </p>
      <p className="t-body-s t-secondary">
        <a href="#inquiry-heading">Ask about a private website quote</a>. A person reviews each request.
      </p>
    </div>
  );
}
